// Pure helpers for the "Custom plans" section (#4341, #5136) — the plans a
// PERSON started, read via `initiated_by=user` (#4340, #4727: `user` = a
// dashboard session or a credential bound to a person; `system` = no person
// is recorded as its initiator — see `CustomPlansSection.tsx`). This module
// holds no fetching: `useCustomPlans` stays in `lib/plan.ts`, this is the
// presenter-facing arithmetic and URL parsing over the `PlanListPage` it
// returns.
//
// After #5136 the section's status / repo / q filters are server-side — the
// request itself carries them, and the response carries `counts` so the tabs
// have their numbers without a second read. What lives here is the verdict
// helpers (`focusPlan`, `planCompletion`, `shortPlanId`) and the URL
// parser, plus the per-section constants (the rail's id prefix, the default
// page number). Client-side filtering over a single page is gone.

import {
  type PlanBucket,
  PlanBucketSchema,
  type PlanListCounts,
  PlanListPageNumberSchema,
  PlanListQSchema,
  type PlanSummary,
  RepoFullNameSchema,
} from '@developerz/domain';

/** The id prefix shared by `<TabList/>` and its panel ids. */
export const CUSTOM_PLANS_TAB_ID_PREFIX = 'custom-plans-status';

/** The URL key for the page-number param — `?custom_page=<n>`. */
export const CUSTOM_PLANS_PAGE_PARAM = 'custom_page';

/** One-based default page when `?custom_page=` is absent or unparseable. */
export const CUSTOM_PLANS_DEFAULT_PAGE = 1;

/** All-zero counts — the tab rail's numbers before the first response lands. */
export const EMPTY_CUSTOM_PLAN_COUNTS: PlanListCounts = {
  all: 0,
  awaiting_approval: 0,
  in_progress: 0,
  stalled: 0,
  completed: 0,
  closed: 0,
};

export type PlanBucketOrAll = PlanBucket | 'all';

/**
 * The parsed URL params the section reads. Every field has a parsed type
 * (`string | null`, `number`); unknown `?status=` falls back to `all`,
 * empty `?repo=` to `null`, unknown `?custom_page=` to 1, missing `?q=`
 * to `null`. The section treats a non-null `q` as the user's search and
 * `null` as "no search".
 */
export type CustomPlanParams = {
  readonly status: PlanBucketOrAll;
  readonly repo: string | null;
  readonly q: string | null;
  readonly page: number;
};

/** A read of one search param from `useSearchParams`'s shaped value. */
function one(params: Partial<Record<string, string | string[]>>, key: string): string | null {
  const v = params[key];
  if (typeof v === 'string') return v;
  return null;
}

/**
 * Parse the section's four URL params from `useSearchParams`' value.
 *
 *   - `?status=` — must `PlanBucketSchema.safeParse`; unknown falls back to
 *     `'all'` (and the section sends no `bucket=` to the API).
 *   - `?repo=` — must `RepoFullNameSchema.safeParse` (the same "owner/repo"
 *     shape `GET /v1/plans` checks); anything else, including empty, falls
 *     back to `null` — an unchecked malformed `?repo=` would reach the
 *     request and 400 there instead.
 *   - `?q=` — must `PlanListQSchema.safeParse` (non-empty, at most
 *     `PLAN_LIST_Q_MAX`); anything else falls back to `null`, for the same
 *     reason.
 *   - `?custom_page=` — must be a positive integer (`PlanListPageNumberSchema`
 *     is the same bound `GET /v1/plans` uses); unknown falls back to 1.
 *
 * Anything that fails to parse falls back rather than throws — the URL is
 * not a place a malformed input should crash the page.
 */
export function parseCustomPlanParams(
  params: Partial<Record<string, string | string[]>>,
): CustomPlanParams {
  const statusRaw = one(params, 'status');
  const statusParsed = statusRaw === null ? null : PlanBucketSchema.safeParse(statusRaw);
  const status: PlanBucketOrAll = statusParsed?.success === true ? statusParsed.data : 'all';

  const repoRaw = one(params, 'repo');
  const repoParsed = repoRaw === null ? null : RepoFullNameSchema.safeParse(repoRaw);
  const repo = repoParsed?.success === true ? repoParsed.data : null;

  const qRaw = one(params, 'q');
  const qParsed = qRaw === null ? null : PlanListQSchema.safeParse(qRaw);
  const q = qParsed?.success === true ? qParsed.data : null;

  const pageRaw = one(params, CUSTOM_PLANS_PAGE_PARAM);
  let page = CUSTOM_PLANS_DEFAULT_PAGE;
  if (pageRaw !== null) {
    const asNumber = Number(pageRaw);
    const parsed = PlanListPageNumberSchema.safeParse(asNumber);
    if (parsed.success) page = parsed.data;
  }

  return { status, repo, q, page };
}

/**
 * How complete one plan is, as the section's progress ring and pill render it.
 *
 * A CANCELLED plan is its own kind, never a percentage — {@link PlanSummary}
 * carries no `cancelledCount`, so there is no "0 of N, minus the dead ones" to
 * report, only "stopped" plus however far it got before that. Do not invent a
 * `cancelledCount`: this issue explicitly leaves the count off `PlanSummary`.
 */
export type PlanCompletion =
  | { kind: 'progress'; landed: number; total: number; pct: number }
  | { kind: 'cancelled'; landed: number; total: number };

/**
 * `landed = mergedCount`, `total = groupCount`, exactly what the API reports —
 * no re-derivation from `stalledCount` or anything else the projection core
 * already settled.
 *
 * `groupCount` is `.positive()` on {@link PlanSummary}'s schema, so `total ===
 * 0` should never reach here; guarded anyway (`pct: 0` rather than `NaN`)
 * because this is a presenter boundary and a schema change elsewhere must not
 * turn into a rendered `NaN%` here.
 */
export function planCompletion(plan: PlanSummary): PlanCompletion {
  const { mergedCount: landed, groupCount: total } = plan;
  if (plan.status === 'cancelled') return { kind: 'cancelled', landed, total };
  const pct = total > 0 ? Math.round((landed / total) * 100) : 0;
  return { kind: 'progress', landed, total, pct };
}

/**
 * The epic label rendered on a row: the last 6 hex characters of the plan id's
 * hex body, lowercase. Plan ids are `<prefix>_<hex>` (`run_…`, `tsk_…`,
 * `rtk_…`); the hex body always outruns 6 characters, so slicing the last 6 of
 * the WHOLE id is equivalent to slicing the last 6 of the hex body alone and
 * needs no prefix-stripping step.
 */
export function shortPlanId(planId: string): string {
  return planId.slice(-6).toLowerCase();
}

/**
 * What the `?plan=<id>` deep link resolves to against the section's CURRENT
 * page rows (#5136). TWO outcomes, never a nullable collapse — mirrors
 * {@link focusBlockers} (`lib/blockers.ts`): "no deep link" and "that id is
 * not in this page" are different facts with different remedies (clear a
 * filter vs. nothing to do), and a `null`/empty-array answer would erase the
 * difference. The single not-in-list notice is shown to the reader; the
 * section does NOT look across pages (#5136: a deep link off-page says so
 * and links to the full-view page, not a server lookup).
 */
export type PlanFocus =
  | { kind: 'none' }
  | { kind: 'found'; plan: PlanSummary }
  | { kind: 'not_in_list'; planId: string };

/** Resolve `rows` against the `?plan=` param. `null`/`''` means "no deep link". */
export function focusPlan(rows: readonly PlanSummary[], planId: string | null): PlanFocus {
  if (planId === null || planId === '') return { kind: 'none' };
  const plan = rows.find((p) => p.planId === planId);
  return plan === undefined ? { kind: 'not_in_list', planId } : { kind: 'found', plan };
}

/**
 * Which empty-state copy a bucket-empty response earns (#5136). The response's
 * `counts.all` is tallied over EVERY bucket under the account's CURRENT `repo`/
 * `q` filter (`listPlansByAccount`, `packages/db`) — never bucket-narrowed — so
 * it is the one number that can tell "this account has never had a custom
 * plan" apart from "this filter matches none" or "this TAB has none".
 *
 * `no_matches` covers the latter two on purpose (pinned interfaces, #5136): a
 * filtered-to-zero read and an empty tab share one copy. `account_empty` is
 * reserved for the one case neither of those is — no repo/q filter active AND
 * `counts.all === 0` — because that is the only shape this response can prove
 * means "never had one", rather than "not under today's view".
 *
 * Callers gate on `data.plans.length === 0 && data.page <= 1` before asking
 * (`CustomPlansPanels`' `isEmpty`) — never a bare `total === 0`; `PanelBody`'s
 * own `rows.length > 0` fallback calls this same function for its "no rows on
 * THIS page" empty slot too. This function only picks which of the two
 * messages fits once a caller has already decided the state is empty.
 */
export type CustomPlansEmptyKind = 'account_empty' | 'no_matches';

export function customPlansEmptyKind(
  counts: PlanListCounts,
  filters: { repo: string | null; q: string | null },
): CustomPlansEmptyKind {
  const filtered = filters.repo !== null || filters.q !== null;
  return counts.all === 0 && !filtered ? 'account_empty' : 'no_matches';
}
